import React, { useCallback, useEffect, useState, useMemo } from 'react';
import { API_BASE_URL } from '../config';
import { Session } from './utils/session';
import Sidebar from './Sidebar';
import { toDisplayDate, toInputDate } from '../utils/dateOnly';
import {
  buildUserTaskSummaryList,
  formatTaskMinutes,
  getTaskLabelFromItem,
  taskStartsInDateRange,
} from '../utils/taskTime';
import UserTaskDetailModal from './UserTaskDetailModal';
import PageLoader, { LoaderButtonLabel } from './PageLoader';
import { downloadCsv, downloadExcel, stampFileDate } from '../utils/exportCsv';
import './TaskBoard/TaskBoard.css';

const PAGE_SIZE = 15;

function buildPageNumbers(current, totalPages) {
  if (totalPages <= 7) {
    return Array.from({ length: totalPages }, (_, i) => i + 1);
  }
  const pages = [1];
  const start = Math.max(2, current - 1);
  const end = Math.min(totalPages - 1, current + 1);
  if (start > 2) pages.push('...');
  for (let i = start; i <= end; i++) pages.push(i);
  if (end < totalPages - 1) pages.push('...');
  pages.push(totalPages);
  return pages;
}
const authHeaders = () => {
  const token = Session.getToken();
  return {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${token}`,
  };
};

function getDefaultDateRange() {
  const today = new Date();
  const todayStr = toInputDate(today);
  const minStart = new Date(today);
  minStart.setMonth(minStart.getMonth() - 2);
  const minStartStr = toInputDate(minStart);
  const sevenDaysAgo = new Date(today);
  sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
  let startStr = toInputDate(sevenDaysAgo);
  if (startStr < minStartStr) startStr = minStartStr;
  return { todayStr, minStartStr, startStr };
}

const StartStopActivity = () => {
  const defaultRange = useMemo(() => getDefaultDateRange(), []);
  const { todayStr: initialTodayStr, minStartStr: minStartDefault, startStr: sevenDaysAgoDefault } = defaultRange;
  const getTodayStr = () => toInputDate(new Date());
  const [showMobileSidebar, setShowMobileSidebar] = useState(false);
  const [activity, setActivity] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [employeeName, setEmployeeName] = useState('');
  const [employeeInput, setEmployeeInput] = useState('');
  const [taskInput, setTaskInput] = useState('');
  const [taskName, setTaskName] = useState('');
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [mode, setMode] = useState('daily');
  const [selectedWorkspace, setSelectedWorkspace] = useState('');
  const [workspaces, setWorkspaces] = useState([]);
  const [statCards, setStatCards] = useState([]);

  
  // initialize applied date range and separate input buffers so
  // selecting dates does not apply filtering until Search is clicked
  const [startDate, setStartDate] = useState(sevenDaysAgoDefault);
  const [endDate, setEndDate] = useState(initialTodayStr);
  const [startInput, setStartInput] = useState(sevenDaysAgoDefault);
  const [endInput, setEndInput] = useState(initialTodayStr);
  const [hideDesktop, setHideDesktop] = useState(false);
  const [pageNumber, setPageNumber] = useState(1);
  const [refreshing, setRefreshing] = useState(false);
  const [taskModal, setTaskModal] = useState(null);

  const toggleSidebar = () => {
    if (window.innerWidth <= 991) {
      setShowMobileSidebar(s => !s);
    } else {
      setHideDesktop(s => !s);
    }
  };

  useEffect(() => {
    const onResize = () => {
      if (window.innerWidth > 991) {
        setShowMobileSidebar(false);
      }
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const fetchActivityList = useCallback(async (silent = false) => {
    if (silent) setRefreshing(true);
    else setLoading(true);
    setError(null);
    try {
      const response = await fetch(
        `${API_BASE_URL}/api/StartStop/Start_Stop_ALL`,
        {
          method: 'GET',
          headers: authHeaders(),
        }
      );

      if (!response.ok) {
        let message = `Failed to load activity list (HTTP ${response.status})`;
        try {
          const errBody = await response.json();
          message = errBody.detail || errBody.message || message;
        } catch {
          // ignore
        }
        throw new Error(message);
      }

      const data = await response.json();
      const records = Array.isArray(data) ? data : [];
      setActivity(records);
    } catch (err) {
      setError(err.message);
      setActivity([]);
    } finally {
      if (silent) setRefreshing(false);
      else setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchActivityList();
  }, [fetchActivityList]);

  const applyDefaultFilters = useCallback(() => {
    const range = getDefaultDateRange();
    setEmployeeInput('');
    setEmployeeName('');
    setTaskInput('');
    setTaskName('');
    setStartInput(range.startStr);
    setEndInput(range.todayStr);
    setStartDate(range.startStr);
    setEndDate(range.todayStr);
    setPageNumber(1);
    return range;
  }, []);

  const handleSearch = () => {
    const nextStart = startInput || '';
    const nextEnd = endInput || '';
    if (!nextStart || !nextEnd) return;
    setStartDate(nextStart);
    setEndDate(nextEnd);
    setEmployeeName(employeeInput || '');
    setTaskName(taskInput || '');
    setPageNumber(1);
    fetchActivityList(true);
  };

  const handleClearFilter = async () => {
    applyDefaultFilters();
    await fetchActivityList(true);
  };

  useEffect(() => {
    setWorkspaces([
      { workspaceID: '1', workspaceName: 'Workspace 1' },
      { workspaceID: '2', workspaceName: 'Workspace 2' },
    ]);
  
  }, [activity, employeeName, mode]);

  const getTaskLabel = getTaskLabelFromItem;

  const taskOptions = useMemo(() => {
    if (!startDate || !endDate) return [];

    const names = new Set();
    activity.forEach((item) => {
      if (!taskStartsInDateRange(item, startDate, endDate)) return;

      if (employeeName) {
        const username = (item.userName || item.UserName || '').trim();
        if (username.toLowerCase() !== employeeName.toLowerCase()) return;
      }

      const name = getTaskLabel(item);
      if (name) names.add(name);
    });

    return [...names].sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }));
  }, [activity, startDate, endDate, employeeName]);

  const userOptions = useMemo(() => {
    const names = new Set();
    activity.forEach((item) => {
      const username = (item.userName || item.UserName || '').trim();
      if (username) names.add(username);
    });
    return [...names].sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }));
  }, [activity]);

  useEffect(() => {
    if (!taskInput) return;
    if (!taskOptions.includes(taskInput)) {
      setTaskInput('');
      setTaskName('');
    }
  }, [taskOptions, taskInput]);

  const userTaskRows = useMemo(
    () => buildUserTaskSummaryList(activity, { startDate, endDate, taskName, employeeName }),
    [activity, startDate, endDate, taskName, employeeName]
  );

  const totalCount = userTaskRows.length;
  const totalPages = Math.max(1, Math.ceil(totalCount / PAGE_SIZE));

  const paginatedActivity = useMemo(() => {
    const start = (pageNumber - 1) * PAGE_SIZE;
    return userTaskRows.slice(start, start + PAGE_SIZE);
  }, [userTaskRows, pageNumber]);

  const rangeStart = totalCount === 0 ? 0 : (pageNumber - 1) * PAGE_SIZE + 1;
  const rangeEnd = Math.min(pageNumber * PAGE_SIZE, totalCount);
  const pageNumbers = buildPageNumbers(pageNumber, totalPages);

  useEffect(() => {
    setPageNumber(1);
  }, [employeeName, taskName, startDate, endDate]);

  useEffect(() => {
    if (pageNumber > totalPages) {
      setPageNumber(totalPages);
    }
  }, [pageNumber, totalPages]);

  const openTaskModal = (row) => {
    setTaskModal({
      userName: row.userName,
      taskName: row.taskName,
      startDate,
      endDate,
    });
  };

  const TASK_EXPORT_HEADERS = [
    'User Name',
    'Task Name',
    'Max Date',
    'Total Time',
  ];

  const buildTaskExportRows = () =>
    userTaskRows.map((row) => [
      row.userName,
      row.taskName,
      row.maxDate ? toDisplayDate(row.maxDate) : '',
      formatTaskMinutes(row.totalMinutes),
    ]);

  const handleExportTaskCsv = () => {
    downloadCsv(
      `task-report-${stampFileDate()}.csv`,
      TASK_EXPORT_HEADERS,
      buildTaskExportRows()
    );
  };

  const handleExportTaskExcel = () => {
    downloadExcel(
      `task-report-${stampFileDate()}.xls`,
      TASK_EXPORT_HEADERS,
      buildTaskExportRows()
    );
  };

  const getLast7Days = () => {
    const days = [];
    const today = new Date();
    for (let i = 6; i >= 0; i--) {
      const d = new Date(today);
      d.setDate(today.getDate() - i);
      const value = d.toISOString().split('T')[0];
      const label = d.toLocaleDateString(undefined, {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
      });
      days.push({ value, label });
    }
    return days;
  };

  const last7Days = getLast7Days();

  // Helper/date-range utilities for datepickers
  const formatDate = (d) => (d instanceof Date ? d.toISOString().split('T')[0] : '');
  const minStartStr = minStartDefault;

  const computeEndMax = (startVal) => {
    const today = getTodayStr();
    if (!startVal) return today;
    const parts = startVal.split('-').map(Number);
    const d = new Date(parts[0], parts[1] - 1, parts[2]);
    d.setMonth(d.getMonth() + 2);
    const maxStr = toInputDate(d);
    return maxStr > today ? today : maxStr;
  };

  useEffect(() => {
    const overlay = document.getElementById('sidebar-overlay');
    if (overlay) {
      overlay.style.display = showMobileSidebar ? 'block' : 'none';
    }
    const sidebar = document.getElementById('sidebar');
    if (sidebar) {
      if (showMobileSidebar) sidebar.classList.add('show-mobile'); else sidebar.classList.remove('show-mobile');
      if (hideDesktop) sidebar.classList.add('hide-desktop'); else sidebar.classList.remove('hide-desktop');
    }
    const content = document.getElementById('content');
    if (content) {
      if (hideDesktop) content.classList.add('full-width'); else content.classList.remove('full-width');
    }
  }, [showMobileSidebar, hideDesktop]);
  if (error) return <div>Error: {error}</div>;
 
  return (
    <div className="app-wrapper">
      <div
        id="sidebar-overlay"
        className="sidebar-overlay"
        onClick={() => setShowMobileSidebar(false)}
      />

     {/* <div id="sidebar" className={showMobileSidebar ? 'open' : ''}>
        
      </div> */}

<Sidebar />

      <div id="content">
      <div className='d-flex align-items-center justify-content-between' style={{ marginTop: 7, marginBottom: 32 }}>
        <div className="d-flex align-items-center">
          <button className="btn btn-toggle me-3" onClick={toggleSidebar}>
            <i className="fas fa-bars" />
          </button>
          <h2 className="mb-0">Task Reports</h2>
        </div>
        <div className="d-flex gap-2">
          <button
            type="button"
            className="btn btn-outline-success btn-sm"
            onClick={handleExportTaskExcel}
            disabled={loading || refreshing || userTaskRows.length === 0}
          >
            <i className="fas fa-file-excel me-1" aria-hidden="true" />
            Export Excel
          </button>
          <button
            type="button"
            className="btn btn-outline-secondary btn-sm"
            onClick={handleExportTaskCsv}
            disabled={loading || refreshing || userTaskRows.length === 0}
          >
            <i className="fas fa-file-csv me-1" aria-hidden="true" />
            Export CSV
          </button>
        </div>
      </div>
      <div className='main-card'>
      <div className="row g-3 mb-4 align-items-end">
          {/* <div className="col-xl-3">
            <div className="filter-box">
              <input
                type="radio"
                className="btn-check"
                id="daily"
                checked={mode === 'daily'}
                onChange={() => setMode('daily')}
              />
              <label className="btn btn-outline-custom px-3" htmlFor="daily">
                Daily
              </label>

              <input
                type="radio"
                className="btn-check"
                id="weekly"
                checked={mode === 'weekly'}
                onChange={() => setMode('weekly')}
              />
              <label className="btn btn-outline-custom px-3" htmlFor="weekly">
                Weekly
              </label>
            </div>
          </div> */}
          <div className="col-xl-2">
            <label className="small text-muted">User</label>
            <select
              className="form-control"
              value={employeeInput}
              onChange={(e) => setEmployeeInput(e.target.value)}
            >
              <option value="">All Users</option>
              {userOptions.map((u) => (
                <option key={u} value={u}>
                  {u}
                </option>
              ))}
            </select>
          </div>

          <div className="col-xl-2">
            <label className="small text-muted">Start Date</label>
            <input
              type="date"
              className="form-control"
              value={startInput}
              min={minStartStr}
              max={getTodayStr()}
              onChange={(e) => {
                let v = e.target.value;
                if (v && v < minStartStr) v = minStartStr;
                setStartInput(v || '');
                if (endInput && v && endInput < v) {
                  setEndInput(v);
                }
              }}
            />
          </div>

          <div className="col-xl-2">
            <label className="small text-muted">End Date</label>
            <input
              type="date"
              className="form-control"
              value={endInput}
              min={startInput || minStartStr}
              max={computeEndMax(startInput)}
              onChange={(e) => setEndInput(e.target.value || '')}
            />
          </div>

          <div className="col-xl-4">
            <label className="small text-muted">Task</label>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <select
                className="form-control"
                value={taskInput}
                onChange={(e) => setTaskInput(e.target.value)}
                style={{ flex: 1 }}
              >
                <option value="">All Tasks</option>
                {taskOptions.map((task) => (
                  <option key={task} value={task}>
                    {task}
                  </option>
                ))}
              </select>
              <button
                type="button"
                className="btn btn-sm btn-primary"
                onClick={handleSearch}
                disabled={loading || refreshing}
              >
                <LoaderButtonLabel loading={loading || refreshing} loadingLabel="Searching...">
                  Search
                </LoaderButtonLabel>
              </button>
              <button
                type="button"
                className="btn btn-sm btn-outline-secondary"
                onClick={handleClearFilter}
                disabled={loading || refreshing}
              >
                <LoaderButtonLabel loading={loading || refreshing} loadingLabel="Clearing...">
                  Clear Filter
                </LoaderButtonLabel>
              </button>
            </div>
          </div>

          {/* removed selectedDate input as requested */}
        </div>
        {/* Table */}
        {loading ? (
          <PageLoader label="Loading task reports..." />
        ) : (
        <div className="epa-page-loader-host">
          {refreshing && (
            <PageLoader label="Updating results..." compact overlay />
          )}
        <table className={`table table-bordered ${refreshing ? 'epa-content-dimmed' : ''}`}>
          <thead>
            <tr>
              <th>User Name</th>
              <th>Task Name</th>
              <th className="task-reports-max-date-col">Max Date</th>
              <th className="task-reports-total-time-col">Total Time</th>
            </tr>
          </thead>
          <tbody>
            {userTaskRows.length === 0 ? (
              <tr>
                <td colSpan={4} style={{ textAlign: 'center' }}>
                  {activity.length === 0
                    ? 'No task activity found. Start a task from the desktop app first.'
                    : `No records between ${toDisplayDate(startDate)} and ${toDisplayDate(endDate)}. Widen the date range or click Clear Filter.`}
                </td>
              </tr>
            ) : (
              paginatedActivity.map((row) => (
                <tr key={`${row.userName}|${row.taskName}`}>
                  <td>
                    <button
                      type="button"
                      className="task-reports-user-link"
                      onClick={() => openTaskModal(row)}
                    >
                      {row.userName}
                    </button>
                  </td>
                  <td>{row.taskName}</td>
                  <td className="task-reports-max-date-col">
                    {row.maxDate ? toDisplayDate(row.maxDate) : '—'}
                  </td>
                  <td className="task-reports-total-time-col">{formatTaskMinutes(row.totalMinutes)}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
        </div>
        )}

        {totalCount > 0 && (
          <div className="tasks-pagination mt-3">
            <span className="tasks-showing-text">
              Showing {rangeStart}-{rangeEnd} of {totalCount}
            </span>
            <button
              type="button"
              disabled={pageNumber <= 1}
              onClick={() => setPageNumber((p) => Math.max(1, p - 1))}
            >
              &lsaquo;
            </button>
            {pageNumbers.map((p, idx) =>
              p === '...' ? (
                <span key={`ellipsis-${idx}`}>...</span>
              ) : (
                <button
                  key={p}
                  type="button"
                  className={pageNumber === p ? 'active' : ''}
                  onClick={() => setPageNumber(p)}
                >
                  {p}
                </button>
              )
            )}
            <button
              type="button"
              disabled={pageNumber >= totalPages}
              onClick={() => setPageNumber((p) => Math.min(totalPages, p + 1))}
            >
              &rsaquo;
            </button>
          </div>
        )}
      </div>
      </div>

      <UserTaskDetailModal
        userName={taskModal?.userName ?? ''}
        taskName={taskModal?.taskName ?? ''}
        startDate={taskModal?.startDate ?? ''}
        endDate={taskModal?.endDate ?? ''}
        records={activity}
        onClose={() => setTaskModal(null)}
      />
    </div>
  );
};

export default StartStopActivity;
